import React from 'react';
import { useTranslation } from 'react-i18next';
import Skeleton from 'react-loading-skeleton';

import './index.less';

import { IngestionSourceModelOutput } from '../../../../../../gamma-api';
import { formatDateTimeShort } from '../../../../../utils/stringUtil';
import IbTypography from '../../../../../components/common/IbTypography';
import IbButton from '../../../../../components/common/IbButton';
import IbIcon from '../../../../../components/common/IbIcon';
import SourceVolume from '../SourceVolume';
import SourceStatus from '../SourceStatus';
import SourceTitle from '../SourceTitle';
import IbContextMenu from '../../../../../components/common/IbContextMenu';

import {
  MAIN_CLASS_NAME,
  BLOCK_CLASS_NAME,
  CONTEXT_CLASS_NAME,
  STATUS_CLASS_NAME,
  DATE_CLASS_NAME,
  VOLUME_CLASS_NAME,
  TYPE_CLASS_NAME,
  SKELETON_CLASS_NAME,
  SKELETON_CONTAINER_CLASS_NAME,
  SKELETON_HEIGHT,
} from './const';

export interface ISourceHeaderProps {
  loading: boolean;
  source?: IngestionSourceModelOutput;
  onSetup?: () => void;
  onIngest?: () => void;
  onDelete?: () => void;
  onRestart?: () => void;
}

const SourceHeader: React.FC<ISourceHeaderProps> = ({ loading, source, onSetup, onIngest, onDelete, onRestart }) => {
  const { t, i18n } = useTranslation();

  if (!source) {
    return (
      <div className={SKELETON_CONTAINER_CLASS_NAME}>
        <Skeleton containerClassName={SKELETON_CLASS_NAME} height={SKELETON_HEIGHT} />
        <Skeleton containerClassName={SKELETON_CLASS_NAME} height={SKELETON_HEIGHT} />
      </div>
    );
  }

  const rolling = loading || !source || !!source.versions.rolling;

  const onSetupClick = () => {
    onSetup?.();
  };

  const onIngestSelect = () => {
    !rolling && onIngest?.();
  };

  const onDeleteSelect = () => {
    !rolling && onDelete?.();
  };

  const onStatusRestart = () => {
    !rolling && onRestart?.();
  };

  const renderType = () => {
    const parameters = source.versions.latest.source.parameters;
    return (
      <div className={`${BLOCK_CLASS_NAME} ${TYPE_CLASS_NAME}`}>
        <IbTypography.Paragraph light strong type="secondary">
          {t('Source header: Type')}
        </IbTypography.Paragraph>
        <SourceTitle name={t(`Source type: ${parameters?.type}`)} type={parameters?.type} />
      </div>
    );
  };

  const renderVolume = () => {
    return (
      <div className={`${BLOCK_CLASS_NAME} ${VOLUME_CLASS_NAME}`}>
        <IbTypography.Paragraph light strong type="secondary">
          {t('Source header: Volume')}
        </IbTypography.Paragraph>
        <IbTypography.Paragraph type="secondary">
          <SourceVolume type={source.versions.latest.source.parameters.type} value={source.volumes.latest} />
        </IbTypography.Paragraph>
      </div>
    );
  };

  const renderDate = () => {
    return (
      <div className={`${BLOCK_CLASS_NAME} ${DATE_CLASS_NAME}`}>
        <IbTypography.Paragraph light strong type="secondary">
          {t('Source header: Date')}
        </IbTypography.Paragraph>
        <IbTypography.Paragraph type="secondary">
          {formatDateTimeShort(source.ingested_on, i18n.language)}
        </IbTypography.Paragraph>
      </div>
    );
  };

  const renderStatus = () => {
    return (
      <div className={`${BLOCK_CLASS_NAME} ${STATUS_CLASS_NAME}`}>
        <IbTypography.Paragraph light strong type="secondary">
          {t('Source header: Status')}
        </IbTypography.Paragraph>
        <SourceStatus
          action={source.versions.latest.source.instruction.action}
          direction="horizontal"
          loading={loading}
          problem={source.problem}
          rolling={rolling}
          stage={source.stage}
          status={source.status}
          stepsStyle="wide"
          onRestart={onStatusRestart}
        />
      </div>
    );
  };

  const renderContext = () => {
    const menuItems = [
      {
        onSelect: onIngestSelect,
        icon: <IbIcon iconName="refresh" />,
        text: t('Source header: Context: Ingest'),
        disabled: rolling,
      },
      {
        onSelect: onDeleteSelect,
        icon: <IbIcon iconName="delete" />,
        text: t('Source header: Context: Delete'),
        disabled: rolling,
      },
    ];

    return (
      <div className={`${BLOCK_CLASS_NAME} ${CONTEXT_CLASS_NAME}`}>
        <IbButton icon={<IbIcon iconName="setting-two" />} type="link" onClick={onSetupClick}>
          {t('Source header: Context: Setup')}
        </IbButton>
        <IbContextMenu menuItems={menuItems}>
          <IbButton icon={<IbIcon iconName="more-one" />} type="icon" />
        </IbContextMenu>
      </div>
    );
  };

  return (
    <div className={MAIN_CLASS_NAME}>
      {renderStatus()}
      {renderType()}
      {renderVolume()}
      {renderDate()}
      {renderContext()}
    </div>
  );
};

export default SourceHeader;
